import React from 'react';
import '@/styles/tabs.css';

/**
 * PillTabs — adapted from the design reference.
 *
 * DEPARTURES:
 *
 * 1. KEYBOARD SUPPORT. The reference renders role="tablist" with role="tab"
 *    children but implements no keyboard handling, so it announces itself as a
 *    tablist and then does not behave like one. Arrow/Home/End navigation and
 *    roving tabindex are added here — an ARIA role is a promise about behaviour.
 *
 * 2. TOKEN NAME. `var(--weight-medium)` in the reference does not exist in our
 *    token layer; mapped to a literal 500.
 *
 * 3. THE SKIN MOVED TO CSS (styles/tabs.css) in phase F. It was a TONES/SIZES
 *    object of inline styles, which cannot express a `::after` — and the tab
 *    ladder needs one, because every rung is 24-36px and every standalone
 *    target owes 44. Inline styles also cannot express `:focus-visible`, so the
 *    component announced itself as a tablist and had no visible focus.
 *
 * The `accent` tone uses --accent-tint as a FILL with --accent-text on top.
 * The accent itself is never the text colour.
 */

const TONES = ['sand', 'accent', 'solid'];
const SIZES = ['sm', 'md', 'lg'];

export function PillTabs({
  items = [],
  value = 0,
  onChange,
  tone = 'sand',
  size = 'md',
  ariaLabel,
  className = '',
  style,
}) {
  const t = TONES.includes(tone) ? tone : 'sand';
  const sz = SIZES.includes(size) ? size : 'md';

  const move = (next) => {
    if (!onChange || !items.length) return;
    onChange((next + items.length) % items.length);
  };

  const onKeyDown = (e) => {
    if (!onChange) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(value + 1); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(value - 1); }
    else if (e.key === 'Home') { e.preventDefault(); move(0); }
    else if (e.key === 'End') { e.preventDefault(); move(items.length - 1); }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`ds-pill-tabs ${className}`.trim()}
      style={{ gap: sz === 'sm' ? 6 : sz === 'lg' ? 12 : 8, ...style }}
    >
      {items.map((label, i) => {
        const active = i === value;
        return (
          <button
            key={label}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={onChange ? () => onChange(i) : undefined}
            className={`ds-pill-tab ds-pill-tab--${t} ds-pill-tab--${sz}`}
            style={onChange ? undefined : { cursor: 'default' }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default PillTabs;
